import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LandingPageDesign2 from "@/pages/LandingPageDesign2";
import EmailRegisterPage from "@/pages/EmailRegisterPage";
import EmailLoginPage from "@/pages/EmailLoginPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ResendVerificationPage from "@/pages/ResendVerificationPage";
import StudentDashboard from "@/pages/StudentDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import StudentProfile from "@/pages/StudentProfile";
import TestPage from "@/pages/TestPage";
import ResultPage from "@/pages/ResultPage";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import { Toaster, toast } from "@/components/ui/sonner";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import FloatingNotice from "@/components/FloatingNotice";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { useEffect } from "react";

// Get backend URL, with fallback for same-origin deployment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Determine API base URL
// If BACKEND_URL is same as current origin or empty, use relative path
const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
const API = BACKEND_URL && BACKEND_URL !== currentOrigin 
  ? `${BACKEND_URL}/api` 
  : '/api';

console.log('API Configuration:', { BACKEND_URL, currentOrigin, API });

// Set up axios defaults
axios.defaults.baseURL = API;
axios.defaults.withCredentials = true; // Enable cookies for refresh token

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-xl font-semibold text-indigo-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check role - master_admin has access to admin routes
  const isAdmin = user?.role === 'admin' || user?.role === 'master_admin';
  
  if (requiredRole === 'admin' && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  if (requiredRole === 'student' && isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// Public Route - redirects authenticated users
const PublicRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-xl font-semibold text-indigo-600">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    const isAdmin = user?.role === 'admin' || user?.role === 'master_admin';
    return <Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />;
  }

  return children;
};

// Dashboard wrapper with auto-logout
const DashboardWrapper = ({ children }) => {
  const { user, logout } = useAuth();

  // Auto-logout after 5 minutes of inactivity
  useEffect(() => {
    if (!user) return;

    let inactivityTimer;

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        logout();
        toast.error('Logged out due to inactivity');
      }, 5 * 60 * 1000); // 5 minutes
    };

    // Events to track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      clearTimeout(inactivityTimer);
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [user, logout]);

  return children;
};

function AppRoutes() {
  const { user, logout, refreshUser } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPageDesign2 onNavigateToLogin={() => window.location.href = '/login'} />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <EmailLoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <EmailRegisterPage />
          </PublicRoute>
        }
      />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/resend-verification" element={<ResendVerificationPage />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />

      {/* Protected Student Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredRole="student">
            <DashboardWrapper>
              <StudentDashboard user={user} onLogout={logout} refreshUser={refreshUser} />
            </DashboardWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/test/:testId"
        element={
          <ProtectedRoute>
            <TestPage user={user} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/result/:attemptId"
        element={
          <ProtectedRoute>
            <ResultPage user={user} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <StudentProfile user={user} />
          </ProtectedRoute>
        }
      />

      {/* Protected Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="admin">
            <DashboardWrapper>
              <AdminDashboard user={user} onLogout={logout} />
            </DashboardWrapper>
          </ProtectedRoute>
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Component to show notice only for logged-in students
const StudentNotice = () => {
  const { user, isAuthenticated } = useAuth();
  const isStudent = user?.role === 'student';
  
  // Only show notice board for authenticated students
  if (!isAuthenticated || !isStudent) return null;
  
  return <FloatingNotice />;
};

function App() {
  return (
    <div className="App">
      <Toaster />
      <PWAInstallPrompt />
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <StudentNotice />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
